import { useMemo, useState, useEffect } from "react";
import { GOLD, NAVY } from "../styles/palette";
import { optimizeRoundTrip, estimateMinutes, mapsDirUrl, fetchOptimizedRoute } from "../lib/route";
import { geocodeAddress } from "../lib/maps";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { RouteMap } from "./RouteMap";
import { fetchClientAvgMap } from "../lib/visitTimes";
import { useIsMobile } from "../lib/useIsMobile";
import { JobDetailModal } from "./JobDetailModal";
import { useBackClose } from "../lib/navStack";

// Phone-sized primary action: the per-service Complete/Undo is the owner's main tap on this
// page, so on a phone it gets a real finger target instead of the 12px btn-sm.
const mobileBtn = { fontSize: 13, padding: "8px 16px", minHeight: 40 };

// ── coord helpers ─────────────────────────────────────────────────
const finite = (v) => Number.isFinite(Number(v));

// A stop card = one (technician, client) group — services are grouped by client
// WITHIN each tech card (+ the unassigned bucket), so a client whose services are
// split across a tech and "unassigned" renders as two cards. Count the same way the
// UI renders, so the headline/badge never disagree with the visible card count.
const stopKey = (j) => (j.technician_id || "none") + "|" + j.client_id;

// Group a flat list of service rows into one STOP per client (a stop = a client's
// whole day = a job_headers row). Services (cleanup/deo) nest inside, cleanup first.
// Collapses the old per-service double-stop into a single card per location.
function groupByClient(jobs) {
  const order = [], by = new Map();
  (jobs || []).forEach((j) => {
    const k = j.client_id || j.id;
    if (!by.has(k)) {
      by.set(k, {
        client_id: j.client_id, client_name: j.client_name,
        client_address: j.client_address, client_zip: j.client_zip,
        dogs: j.dogs, services: [],
      });
      order.push(k);
    }
    by.get(k).services.push(j);
  });
  order.forEach((k) => by.get(k).services.sort(
    (a, b) => (((a.service_type === "deo") ? 1 : 0) - ((b.service_type === "deo") ? 1 : 0))));
  return order.map((k) => by.get(k));
}

// One stop = one client for the day, with its services (cleanup/deo) nested and
// individually completable. Preserves address/zip, dogs, visit notes, the per-service
// Complete/Undo toggle, and terminal-status (skipped/cancelled/missed) display.
function StopRow({ n, stop, leg, jobsDone, toggleJob, needsAddress, cleanMap, onOpenJob }) {
  const isMobile = useIsMobile();
  const services = stop.services || [];
  // Same job modal the Schedule tab opens — reassign tech / skip / reschedule from here
  // instead of hunting the client down on another tab. Any service on the stop is a valid
  // entry point (the modal derives the whole visit from client_id + scheduled_date).
  const openJob = () => { if (onOpenJob && services[0]?.id) onOpenJob(services[0].id); };
  const clickable = !!onOpenJob && services.length > 0;
  const isTerminal = (s) => ["skipped", "cancelled", "missed", "rescheduled"].includes(s.status);
  const anyCompleted = services.some((s) => jobsDone[s.id]);
  const allResolved = services.length > 0 && services.every((s) => jobsDone[s.id] || isTerminal(s));
  const completed = allResolved && anyCompleted;   // green ✓ only when a service was actually completed
  return (
    <div
      className={`route-item${completed ? " completed" : ""}`}
      // Whole row opens the job (matches the Schedule tab). The Complete/Undo button inside
      // stops propagation so completing a service never also pops the modal.
      onClick={clickable ? openJob : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openJob(); } } : undefined}
      title={clickable ? "Open job — reassign, skip, or reschedule" : undefined}
      style={clickable ? { cursor: "pointer" } : undefined}
    >
      <div className={`route-num${completed ? " done" : ""}`}>{completed ? "✓" : allResolved ? "–" : n}</div>
      <div className="route-info">
        <div className="route-name" style={clickable ? { display: "inline-flex", alignItems: "center", gap: 6 } : undefined}>
          {stop.client_name}
          {clickable && <span aria-hidden="true" style={{ fontSize: 14, color: "var(--accent)", fontWeight: 700, lineHeight: 1 }}>›</span>}
          {services.length > 1 && (
            <span style={{ fontSize: 10, background: "var(--accent-weak)", color: NAVY, padding: "1px 6px", borderRadius: 4, marginLeft: 6, fontWeight: 700 }}>
              {services.length} services
            </span>
          )}
          {needsAddress && (
            <span style={{ fontSize: 10, background: "#ffebe6", color: "#bf2600", padding: "1px 6px", borderRadius: 4, marginLeft: 6, fontWeight: 700 }}>
              ⚠️ needs address
            </span>
          )}
        </div>
        <div className="route-addr">{stop.client_address}{stop.client_zip ? `, ${stop.client_zip}` : ""}</div>
        {leg && (
          <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 3 }}>
            🚗 {leg.miles.toFixed(1)} mi · ~{estimateMinutes(leg.miles)} min from {leg.fromLabel === "Home" ? "Home" : "previous stop"} <span style={{ opacity: .7 }}>(est.)</span>
          </div>
        )}
        {cleanMap?.[stop.client_id]?.avgMin != null && (
          <div style={{ fontSize: 11, color: "#0369a1", marginTop: 3, fontWeight: 600 }}>🧹 ~{cleanMap[stop.client_id].avgMin} min typical clean</div>
        )}
        {/* nested per-service rows — each individually completable */}
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
          {services.map((svc) => {
            const done = !!jobsDone[svc.id];
            const terminal = isTerminal(svc);
            return (
              <div key={svc.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", textDecoration: terminal ? "line-through" : "none" }}>
                  {svc.service_type === "deo" ? "💨 Deodorizer" : "🧹 Cleanup"}
                </span>
                {svc.visit_notes && (
                  <span style={{ fontSize: 11, color: "#854d0e", background: "#fef9c3", padding: "2px 6px", borderRadius: 4 }}>📝 {svc.visit_notes}</span>
                )}
                <span style={{ marginLeft: "auto" }}>
                  {terminal
                    ? <span style={{ fontSize: 11, fontWeight: 700, textTransform: "capitalize", color: svc.status === "skipped" ? "#974f0c" : "var(--text2)" }}>{svc.status}</span>
                    : <button className={`btn btn-sm ${done ? "btn-ghost" : "btn-gold"}`} style={isMobile ? mobileBtn : undefined} onClick={(e) => { e.stopPropagation(); toggleJob(svc.id, svc.client_id); }}>{done ? "Undo" : "Complete"}</button>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 11, color: "var(--text2)" }}>{stop.dogs}🐕</div>
      </div>
    </div>
  );
}

// One technician's card: header (name, stops, est. miles/time, Open in Maps,
// Show map), the ordered stop list with per-leg estimates, un-routable jobs at
// the end, the return-to-home leg, and the lazy inline map.
function TechCard({ tech, jobs, clientCoords, jobsDone, toggleJob, cleanMap, onOpenJob }) {
  const isMobile = useIsMobile();
  const [showMap, setShowMap] = useState(false);

  const home = useMemo(() => (
    tech && finite(tech.home_lat) && finite(tech.home_lng)
      ? { lat: Number(tech.home_lat), lng: Number(tech.home_lng) }
      : null
  ), [tech]);

  // Collapse this tech's services into one stop per client, then split into
  // routable (client has coords) vs un-routable.
  const { routable, unroutable, stops } = useMemo(() => {
    const grouped = groupByClient(jobs);
    const r = [], u = [];
    grouped.forEach((s) => {
      const c = clientCoords.get(s.client_id);
      if (c) r.push({ id: s.client_id, lat: c.lat, lng: c.lng, job: s });
      else u.push(s);
    });
    return { routable: r, unroutable: u, stops: grouped };
  }, [jobs, clientCoords]);

  // Real road distance/time matrix for these stops. Falls back to the straight-line
  // estimate until it loads (or if GOOGLE_MAPS_SERVER_KEY isn't set). Refetched when
  // the stop set changes.
  // Road-optimised route from `optimize-route` (ONE Google request; see lib/route.js for why
  // this replaced the per-pair matrix). Null until it loads or if it fails -> straight-line.
  const [roadRoute, setRoadRoute] = useState(null);
  // Key on the STOP SET, not the array's identity. `routable` is rebuilt by useMemo whenever
  // its inputs change identity, so depending on the array itself re-billed Google on every
  // render that touched today's jobs. Same stable-key idiom MyRoutePanel uses.
  const matrixKey = useMemo(
    () => (home ? `${home.lat},${home.lng}|` : "nohome|") + routable.map((s) => s.id).sort().join(","),
    [home, routable],
  );
  useEffect(() => {
    let alive = true;
    setRoadRoute(null);
    if (!home || routable.length === 0) return;
    const authFetch = (url, opts = {}) => fetch(url, { ...opts, headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, ...(opts.headers || {}) } });
    fetchOptimizedRoute(home, routable, SUPABASE_URL, authFetch).then((r) => { if (alive) setRoadRoute(r); });
    return () => { alive = false; };
  }, [matrixKey]); // eslint-disable-line react-hooks/exhaustive-deps -- stop-set key, not identity

  // Prefer the road-optimised answer (already the same shape); otherwise fall back to the
  // free straight-line estimate so the route still renders if Google is unreachable.
  const opt = useMemo(() => {
    if (!home || routable.length === 0) return null;
    return roadRoute || optimizeRoundTrip(home, routable);
  }, [home, routable, roadRoute]);

  const totalMin = opt ? (opt.basis === "road" ? Math.round(opt.totalMinutes) : estimateMinutes(opt.totalMiles)) : 0;

  // Stops for the map + maps URL (in optimized order).
  const mapStops = opt
    ? opt.ordered.map((s) => ({ lat: s.lat, lng: s.lng, label: s.job.client_name }))
    : [];

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)", display: "flex", alignItems: "center", gap: 8 }}>
            {tech.name}
            <span className="badge badge-gray">{stops.length} stop{stops.length === 1 ? "" : "s"}</span>
          </div>
          {opt
            ? <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 4 }}>
                Round trip · {opt.totalMiles.toFixed(1)} mi · ~{totalMin} min <span style={{ opacity: .7 }}>(est.)</span>
              </div>
            : !home
              ? <div style={{ fontSize: 12, color: "#974f0c", marginTop: 4 }}>
                  Add this technician's home address in their profile to optimize the order.
                </div>
              : <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 4 }}>No mappable stops yet.</div>}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {opt && opt.ordered.length > 0 && (
            <>
              <a className="btn btn-gold btn-sm" style={{ textDecoration: "none" }} href={mapsDirUrl(home, opt.ordered.map(s => ({ lat: s.lat, lng: s.lng })))} target="_blank" rel="noopener noreferrer">
                🧭 Open in Maps
              </a>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowMap((v) => !v)}>
                {showMap ? "Hide map" : "Show map"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Optimized ordered stops (1..N). */}
      {opt
        ? opt.ordered.map((s, i) => (
            <StopRow key={s.job.client_id} n={i + 1} stop={s.job} leg={opt.legs[i]} jobsDone={jobsDone} toggleJob={toggleJob} cleanMap={cleanMap} onOpenJob={onOpenJob} />
          ))
        // No home (or no routable) → list routable stops unordered (no legs).
        : routable.map((s, i) => (
            <StopRow key={s.job.client_id} n={i + 1} stop={s.job} jobsDone={jobsDone} toggleJob={toggleJob} cleanMap={cleanMap} onOpenJob={onOpenJob} />
          ))}

      {/* Return-to-home leg + day total. */}
      {opt && opt.ordered.length > 0 && (() => {
        const road = opt.basis === "road";
        const lastLeg = opt.legs[opt.legs.length - 1];
        const lastMin = road ? Math.round(lastLeg.minutes) : estimateMinutes(lastLeg.miles);
        const basisHint = road ? "Real driving distance + time from Google" : "Straight-line estimate — add a routing key to upgrade";
        return (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "10px 16px", marginTop: 4, borderRadius: "var(--r-sm)", background: "var(--accent-weak)" }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>🏠</div>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--text2)" }}>
            Return to Home · {lastLeg.miles.toFixed(1)} mi · ~{lastMin} min {!road && <span style={{ opacity: .7 }}>(est.)</span>}
          </div>
          {/* On a phone the day total drops to its own line under the return leg instead of
              wrapping word-by-word beside it. The basis hint lives in a hover title on
              desktop; touch has no hover, so it is written out inline there. */}
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", ...(isMobile ? { flexBasis: "100%", paddingLeft: 40 } : {}) }}>
            Day total: {opt.totalMiles.toFixed(1)} mi · ~{totalMin} min
            <span title={isMobile ? undefined : basisHint} style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 4, background: road ? "#e3fcef" : "#eef1f5", color: road ? "#1a7f4b" : "#8a93a2" }}>
              {road ? "🚗 driving" : "straight-line est."}
            </span>
            {isMobile && <div style={{ fontSize: 11, fontWeight: 400, color: "var(--text2)", marginTop: 2 }}>{basisHint}</div>}
          </div>
        </div>
        );
      })()}

      {/* Un-routable jobs (no client coords) — rendered at the end, flagged. */}
      {unroutable.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#974f0c", marginBottom: 6 }}>
            {unroutable.length} stop{unroutable.length === 1 ? "" : "s"} can't be routed (missing map coordinates)
          </div>
          {unroutable.map((s) => (
            <StopRow key={s.client_id} n={"—"} stop={s} jobsDone={jobsDone} toggleJob={toggleJob} needsAddress cleanMap={cleanMap} />
          ))}
        </div>
      )}

      {showMap && opt && opt.ordered.length > 0 && (
        <RouteMap home={home} stops={mapStops} />
      )}
    </div>
  );
}

export function Route({ todayJobs, jobsDone, toggleJob, manualJobs, onCompleteManualJob, technicians = [], clients = [], onRefreshClients, setPage, pricing, showToast, onRefresh }) {
  const isMobile = useIsMobile();
  const [geocoding, setGeocoding] = useState(false);
  const [geoMsg, setGeoMsg] = useState("");
  const [cleanMap, setCleanMap] = useState({}); // {client_id:{avgMin,visits}} — GPS typical-clean per yard
  useEffect(() => { fetchClientAvgMap().then(setCleanMap); }, []);
  // Job detail modal (shared with the Schedule tab) — raw job.id when open. Mirrors
  // Schedule.js exactly, including the phone back-gesture closing the modal, not the tab.
  const [jobDetailId, setJobDetailId] = useState(null);
  useBackClose(!!jobDetailId, () => setJobDetailId(null));

  // client_id -> {lat,lng} for clients with valid numeric coords.
  const clientCoords = useMemo(() => {
    const m = new Map();
    (clients || []).forEach((c) => {
      if (finite(c.lat) && finite(c.lng)) m.set(c.id, { lat: Number(c.lat), lng: Number(c.lng) });
    });
    return m;
  }, [clients]);

  // Today's non-manual jobs grouped by technician (+ an Unassigned bucket).
  const { techGroups, unassigned, allJobs } = useMemo(() => {
    const jobs = (todayJobs || []).filter((j) => j.source !== "manual");
    const byTech = new Map();
    const noTech = [];
    jobs.forEach((j) => {
      if (j.technician_id) {
        if (!byTech.has(j.technician_id)) byTech.set(j.technician_id, []);
        byTech.get(j.technician_id).push(j);
      } else {
        noTech.push(j);
      }
    });
    const groups = [];
    byTech.forEach((groupJobs, techId) => {
      const tech = (technicians || []).find((t) => t.id === techId) ||
        { id: techId, name: groupJobs[0]?.technician_name || "Technician" };
      groups.push({ tech, jobs: groupJobs });
    });
    // Stable order by tech name.
    groups.sort((a, b) => (a.tech.name || "").localeCompare(b.tech.name || ""));
    return { techGroups: groups, unassigned: noTech, allJobs: jobs };
  }, [todayJobs, technicians]);

  // Progress across ALL of today's (non-manual) services; the headline count is STOPS.
  const done = allJobs.filter((j) => jobsDone[j.id]).length;
  const pct = allJobs.length ? Math.round((done / allJobs.length) * 100) : 0;
  const stopCount = new Set(allJobs.map(stopKey)).size;
  const unassignedStops = useMemo(() => groupByClient(unassigned), [unassigned]);

  // Active clients missing coords that still have an address to geocode.
  const missingCoords = useMemo(() => (
    (clients || []).filter((c) => c.active !== false && (!finite(c.lat) || !finite(c.lng)) && c.address && c.zip)
  ), [clients]);

  // Count of un-routable stops actually in today's route (for the banner copy).
  const unroutableToday = new Set(allJobs.filter((j) => !clientCoords.has(j.client_id)).map(stopKey)).size;

  async function handleGeocode() {
    if (geocoding) return;
    setGeocoding(true);
    setGeoMsg("");
    let ok = 0;
    const failures = [];
    for (const c of missingCoords) {
      try {
        const coords = await geocodeAddress(c.address, c.zip);
        if (coords && finite(coords.lat) && finite(coords.lng)) {
          const res = await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${c.id}`, {
            method: "PATCH",
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${getAuthToken()}`,
              "Content-Type": "application/json",
              Prefer: "return=representation",
            },
            body: JSON.stringify({ lat: coords.lat, lng: coords.lng }),
          });
          const rows = await res.json().catch(() => null);
          if (res.ok && Array.isArray(rows) && rows.length > 0) ok++;
          else failures.push(c.name || c.id);
        } else {
          failures.push(c.name || c.id);
        }
      } catch (e) {
        failures.push(c.name || c.id);
      }
    }

    // Refetch clients so routes recompute with the new coords.
    if (ok > 0 && onRefreshClients) {
      try {
        const fresh = await fetch(`${SUPABASE_URL}/rest/v1/clients?order=name.asc`, {
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` },
        }).then((r) => r.json());
        if (Array.isArray(fresh)) onRefreshClients(fresh);
      } catch (e) { /* keep going; the message still reflects the pass */ }
    }

    setGeocoding(false);
    setGeoMsg(
      `Geocoded ${ok}` +
      (failures.length ? ` · ${failures.length} failed (fix their address): ${failures.slice(0, 5).join(", ")}${failures.length > 5 ? "…" : ""}` : "")
    );
  }

  const manualToday = manualJobs || [];

  return (
    <div>
      <div className="today-banner" style={{ marginBottom: 20 }}>
        <div>
          <h3>Today's Route</h3>
          <p>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
          <div style={{ marginTop: 8, background: "rgba(255,255,255,.15)", borderRadius: 8, height: 6, width: 240, maxWidth: "100%" }}>
            <div style={{ background: GOLD, height: 6, borderRadius: 8, width: `${pct}%`, transition: ".4s" }} />
          </div>
          <div style={{ fontSize: 12, marginTop: 4, opacity: .7 }}>{done} of {allJobs.length} service{allJobs.length === 1 ? "" : "s"} done ({pct}%)</div>
          {setPage && (
            <button className="btn btn-sm" onClick={() => setPage("clientmap")} style={{ marginTop: 10, background: "rgba(255,255,255,.18)", color: "#fff", border: "1px solid rgba(255,255,255,.35)" }}>
              🗺 Open Client Map
            </button>
          )}
        </div>
        <div className="today-num" title={`${allJobs.length} services`}>{stopCount}</div>
      </div>

      {/* Geocode backfill banner — only when some of today's stops can't route. */}
      {missingCoords.length > 0 && unroutableToday > 0 && (
        <div className="card" style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, borderColor: "rgba(215,50,43,.25)" }}>
          <div style={{ fontSize: 13, color: "var(--text)" }}>
            <strong>{unroutableToday}</strong> stop{unroutableToday === 1 ? "" : "s"} today {unroutableToday === 1 ? "is" : "are"} missing map coordinates and can't be routed.
            <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>
              {missingCoords.length} active client{missingCoords.length === 1 ? "" : "s"} with an address can be geocoded.
            </div>
            {geoMsg && <div style={{ fontSize: 12, color: "var(--ok)", marginTop: 4 }}>{geoMsg}</div>}
          </div>
          <button className="btn btn-gold btn-sm" onClick={handleGeocode} disabled={geocoding}>
            {geocoding ? "Geocoding…" : "📍 Geocode them"}
          </button>
        </div>
      )}

      {/* Manual jobs for today — preserved. */}
      {manualToday.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Inter", fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            ⭐ Manual Jobs Today ({manualToday.length})
          </div>
          {manualToday.map((j) => (
            <div key={j.id} className="route-item" style={{ borderColor: GOLD, borderWidth: 2 }}>
              <div className="route-num" style={{ background: GOLD, color: NAVY }}>★</div>
              <div className="route-info">
                <div className="route-name">{j.client_name}</div>
                <div className="route-addr">{j.notes || "One-time service"}</div>
                {j.time && <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2 }}>🕐 {j.time}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                {j.cost > 0 && <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 4 }}>${Number(j.cost).toFixed(2)}</div>}
                <button className="btn btn-gold btn-sm" style={isMobile ? mobileBtn : undefined} onClick={() => onCompleteManualJob && onCompleteManualJob(j)}>
                  Complete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Per-technician optimized routes. */}
      {allJobs.length === 0
        ? <div className="empty"><p>No jobs today</p></div>
        : techGroups.map(({ tech, jobs }) => (
            <TechCard key={tech.id} tech={tech} jobs={jobs} clientCoords={clientCoords} jobsDone={jobsDone} toggleJob={toggleJob} cleanMap={cleanMap} onOpenJob={setJobDetailId} />
          ))}

      {/* Unassigned jobs — no tech/home, so no optimization. Still listed. */}
      {unassignedStops.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
            Unassigned
            <span className="badge badge-gray">{unassignedStops.length} stop{unassignedStops.length === 1 ? "" : "s"}</span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 12 }}>
            Assign these jobs to a technician to include them in an optimized route.
          </div>
          {unassignedStops.map((s, i) => (
            <StopRow
              key={s.client_id}
              n={i + 1}
              stop={s}
              jobsDone={jobsDone}
              toggleJob={toggleJob}
              needsAddress={!clientCoords.has(s.client_id)}
              cleanMap={cleanMap}
              onOpenJob={setJobDetailId}
            />
          ))}
        </div>
      )}

      {/* Job detail modal — same one the Schedule tab opens. Reassign / skip / reschedule
          right from the route instead of hunting the client down on another tab. */}
      {jobDetailId && (() => {
        const rawJob = (todayJobs || []).find((j) => j.id === jobDetailId);
        if (!rawJob) { setJobDetailId(null); return null; }
        return (
          <JobDetailModal
            job={rawJob}
            clients={clients}
            technicians={technicians}
            pricing={pricing}
            onClose={() => setJobDetailId(null)}
            onUpdated={() => { onRefresh && onRefresh(); }}
            onCompleteManualJob={onCompleteManualJob}
            showToast={showToast}
          />
        );
      })()}
    </div>
  );
}
